import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FocusScope } from '@react-aria/focus';
import {
  DismissButton,
  useOverlay,
  usePreventScroll
} from '@react-aria/overlays';
import cn from 'classnames';

import { DEFAULT_STYLES } from '../../constants';
import {
  getBottomSheetMaxHeight,
  getKeyboardInset,
  getVisualViewportBounds,
  VisualViewportBounds
} from '../../utils/viewportHeightUtils';

import { Portal } from '../Portal';
import {
  BottomSheetBackdrop,
  BottomSheetContainer,
  BottomSheetContent,
  BottomSheetFooter
} from './styledComponents';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  className?: string;
  showCloseButton?: boolean;
  topContent?: React.ReactNode;
  footer?: React.ReactNode;
}

export const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  title,
  children,
  className,
  showCloseButton = true,
  topContent,
  footer
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isClosingRef = useRef(false);
  const [availableHeight, setAvailableHeight] = useState<number | null>(null);
  const [keyboardInset, setKeyboardInset] = useState(0);
  const [viewportBounds, setViewportBounds] =
    useState<VisualViewportBounds | null>(null);

  usePreventScroll({ isDisabled: !isOpen });

  useEffect(() => {
    if (!isOpen) return;

    const updateViewportMetrics = () => {
      if (window.visualViewport) {
        const visualViewport = window.visualViewport;
        const viewportHeight = visualViewport.height;

        setAvailableHeight(viewportHeight);
        setKeyboardInset(getKeyboardInset(window.innerHeight, visualViewport));
        setViewportBounds(
          getVisualViewportBounds(
            visualViewport,
            window.innerWidth,
            window.innerHeight
          )
        );
      } else {
        const windowHeight = window.innerHeight;
        setAvailableHeight(windowHeight * 0.8);
        setKeyboardInset(0);
        setViewportBounds(null);
      }
    };

    updateViewportMetrics();

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewportMetrics);
      window.visualViewport.addEventListener('scroll', updateViewportMetrics);
    } else {
      window.addEventListener('resize', updateViewportMetrics);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          'resize',
          updateViewportMetrics
        );
        window.visualViewport.removeEventListener(
          'scroll',
          updateViewportMetrics
        );
      } else {
        window.removeEventListener('resize', updateViewportMetrics);
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !contentRef.current || footer) return;

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        requestAnimationFrame(() => {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        });
      }
    };

    const contentElement = contentRef.current;
    contentElement.addEventListener('focusin', handleFocusIn);

    return () => {
      contentElement.removeEventListener('focusin', handleFocusIn);
    };
  }, [isOpen, footer]);

  useEffect(() => {
    if (isOpen) {
      isClosingRef.current = false;
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    onClose();
    setTimeout(() => {
      isClosingRef.current = false;
    }, 300);
  }, [onClose]);

  const { overlayProps, underlayProps } = useOverlay(
    {
      isOpen,
      onClose: handleClose,
      isDismissable: true,
      shouldCloseOnBlur: false
    },
    overlayRef
  );

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose]
  );

  if (!isOpen) return null;

  const maxHeight = getBottomSheetMaxHeight(
    availableHeight,
    window.innerHeight,
    { keyboardInset }
  );
  const isKeyboardOpen = keyboardInset > 0;
  const sheetHeight = isKeyboardOpen ? maxHeight : undefined;

  const overlayStyle: React.CSSProperties | undefined = viewportBounds
    ? {
      top: viewportBounds.offsetTop,
      left: viewportBounds.offsetLeft,
      width: viewportBounds.width,
      height: viewportBounds.height,
      right: 'auto',
      bottom: 'auto'
    }
    : undefined;

  return (
    <Portal>
      <div
        {...underlayProps}
        ref={overlayRef}
        className={DEFAULT_STYLES.bottomSheet__overlay}
        style={overlayStyle}
        onClick={handleOverlayClick}
      >
        <BottomSheetBackdrop
          className={cn(isOpen ? 'opacity-100' : 'opacity-0')}
        />

        <FocusScope contain restoreFocus autoFocus>
          <BottomSheetContainer
            {...overlayProps}
            ref={sheetRef}
            $maxHeight={maxHeight}
            $height={sheetHeight}
            $keyboardInset={keyboardInset}
            $constrainToVisualViewport={Boolean(viewportBounds)}
            className={cn(
              DEFAULT_STYLES.bottomSheet,
              'transform transition-transform duration-300 ease-out',
              isOpen ? 'translate-y-0' : 'translate-y-full',
              className,
              'base__bottom__sheet'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <DismissButton onDismiss={handleClose} />

            {(title || showCloseButton) && (
              <div className={DEFAULT_STYLES.bottomSheet__header}>
                {title && (
                  <h3 className={DEFAULT_STYLES.bottomSheet__title}>{title}</h3>
                )}
                {showCloseButton && (
                  <button
                    type="button"
                    onClick={handleClose}
                    className={DEFAULT_STYLES.bottomSheet__closeButton}
                    aria-label="Close"
                  >
                    <svg
                      className="w-5 h-5 text-cement"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            )}

            {topContent}

            <BottomSheetContent
              ref={contentRef}
              className={DEFAULT_STYLES.bottomSheet__content}
            >
              {children}
            </BottomSheetContent>

            {footer && (
              <BottomSheetFooter className={DEFAULT_STYLES.bottomSheet__footer}>
                {footer}
              </BottomSheetFooter>
            )}

            <DismissButton onDismiss={handleClose} />
          </BottomSheetContainer>
        </FocusScope>
      </div>
    </Portal>
  );
};

BottomSheet.displayName = 'BottomSheet';
